import { useQuery } from "@tanstack/react-query";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";
import { TOKEN_CONFIG, SOCIAL } from "@/lib/config";
import Icon from "@/components/shared/Icon";
import Seo from "@/components/shared/Seo";
import PageHero from "@/components/shared/PageHero";

function Row({ label, value, mono }) {
  return (
    <div className="flex flex-col gap-1 border-b border-[#147DEB]/10 py-4 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-sm font-semibold text-[#64748B]">{label}</span>
      <span className={`text-sm font-bold text-[#082B66] ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

export default function Token() {
  const { t, code } = useLang();
  const { data: cfg } = useQuery({ queryKey: ["config"], queryFn: () => api.get("/config").then((r) => r.data) });

  const contract = cfg?.contract_address ?? null;
  const network = cfg?.network ?? TOKEN_CONFIG.network;

  return (
    <>
      <Seo title={t("token.title")} description={t("token.overviewDesc")} />
      <PageHero eyebrow={t("nav.token")} title={t("token.title")} subtitle={t("token.overviewDesc")} tone="navy" />

      <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        {/* education-first reminder */}
        <div className="rounded-3xl border-2 border-[#45C878]/25 bg-[#EAFBF1] p-5" data-testid="token-education-note">
          <p className="inline-flex items-start gap-2 text-sm font-semibold text-emerald-800">
            <Icon name="GraduationCap" className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#45C878]" />
            {t("footer.wallet")} {t("token.overviewDesc")}
          </p>
        </div>

        {/* key facts */}
        <div className="mt-8 rounded-3xl border-2 border-[#147DEB]/10 bg-white p-6 sm:p-8">
          <h2 className="font-display text-xl font-bold text-[#082B66]">{t("token.overview")}</h2>
          <div className="mt-4">
            <Row label={t("token.network")} value={`${network} · ${code === "zh" ? "计划中" : "Planned"}`} />
            <Row
              label={t("token.contract")}
              mono
              value={
                contract || (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700" data-testid="token-contract-status">
                    <Icon name="Clock" className="h-3.5 w-3.5" /> {t("common.comingSoon")}
                  </span>
                )
              }
            />
            <Row label={t("token.tokenomics")} value={<span className="text-amber-600">{t("common.notAvailable")}</span>} />
            <Row label={t("token.audit")} value={<span className="text-amber-600">{cfg?.audit_status ? (code === "zh" ? "尚未提供" : cfg.audit_status) : t("common.notAvailable")}</span>} />
            <div className="flex flex-col gap-1 py-4 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm font-semibold text-[#64748B]">{t("token.official")}</span>
              {SOCIAL.xUrl ? (
                <a href={SOCIAL.xUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-[#147DEB] underline" data-testid="token-x-link">{SOCIAL.xHandle || "X"}</a>
              ) : (
                <span className="text-sm font-bold text-[#94A3B8]" data-testid="token-x-placeholder">{t("common.comingSoon")}</span>
              )}
            </div>
          </div>

          {/* actions */}
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={cfg?.buy_url || TOKEN_CONFIG.buyUrl} target="_blank" rel="noopener noreferrer" data-testid="token-buy-button" className="inline-flex items-center gap-2 rounded-2xl bg-[#147DEB] px-6 py-3 font-bold text-white hover:bg-[#0F62BD]">
              <Icon name="Coins" className="h-5 w-5" /> {t("token.buy")} <Icon name="ExternalLink" className="h-4 w-4 opacity-70" />
            </a>
            <a href={cfg?.chart_url || TOKEN_CONFIG.chartUrl} target="_blank" rel="noopener noreferrer" data-testid="token-chart-button" className="inline-flex items-center gap-2 rounded-2xl border-2 border-[#147DEB]/20 bg-white px-6 py-3 font-bold text-[#082B66] hover:border-[#147DEB]">
              <Icon name="LineChart" className="h-5 w-5" /> {t("token.chart")} <Icon name="ExternalLink" className="h-4 w-4 opacity-70" />
            </a>
          </div>
          <p className="mt-3 inline-flex items-start gap-2 text-xs text-[#94A3B8]" data-testid="token-chart-note">
            <Icon name="Info" className="mt-0.5 h-4 w-4 flex-shrink-0" /> {t("token.chartNote")} ({t("common.openInNewTab")})
          </p>
        </div>

        {/* charity mechanism */}
        <div className="mt-8 rounded-3xl border-2 border-[#45C878]/20 bg-white p-6 sm:p-8">
          <h2 className="font-display flex items-center gap-2 text-xl font-bold text-[#082B66]"><Icon name="HeartHandshake" className="h-6 w-6 text-[#45C878]" /> {t("token.charity")}</h2>
          <p className="mt-3 text-base leading-relaxed text-[#475569]">{t("token.charityDesc")}</p>
        </div>

        {/* risk */}
        <div className="mt-8 rounded-3xl border-2 border-rose-200 bg-rose-50 p-6 sm:p-8" data-testid="token-risk">
          <h2 className="font-display flex items-center gap-2 text-xl font-bold text-rose-900"><Icon name="TriangleAlert" className="h-6 w-6 text-rose-500" /> {t("token.risk")}</h2>
          <p className="mt-3 text-base leading-relaxed text-rose-800">{t("token.riskDesc")}</p>
        </div>
      </section>
    </>
  );
}
